import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { createSupportRequest } from '@/lib/api';
import { toast } from 'sonner';
import { HeartHandshake, Send, CheckCircle } from 'lucide-react';

const supportTypes = [
  "Behavioral Health Navigation",
  "Family Support Guidance",
  "Veteran Services",
  "Resource Information",
  "Community Wellness",
  "Other"
];

const RequestSupport = () => {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    location: '',
    support_type: '',
    description: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createSupportRequest(formData);
      setSubmitted(true);
      toast.success('Support request submitted successfully!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-brand-neutral py-20" data-testid="request-support-page">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white p-12 rounded-2xl shadow-sm">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-brand-blue mb-4">
              Request Submitted!
            </h1>
            <p className="text-gray-600 mb-8">
              Thank you for reaching out. Our team will review your request and contact you soon.
            </p>
            <Button 
              onClick={() => {
                setSubmitted(false);
                setFormData({
                  full_name: '',
                  email: '',
                  phone: '',
                  location: '',
                  support_type: '',
                  description: ''
                });
              }}
              className="bg-brand-orange hover:bg-brand-orange/90 text-white"
            >
              Submit Another Request
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="request-support-page">
      {/* Hero Section */}
      <section className="bg-brand-blue text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mb-6">
              <HeartHandshake size={32} />
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold mb-6 tracking-tight" data-testid="page-title">
              Request Support
            </h1>
            <p className="text-lg text-white/90 leading-relaxed">
              Fill out the form below to request navigation support. Our team will review your request and connect you with appropriate resources.
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16 bg-brand-neutral">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-white rounded-2xl shadow-sm">
            <CardContent className="p-8">
              <h2 className="font-heading text-2xl font-bold text-brand-blue mb-6">
                Navigation Support Request Form
              </h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => handleChange('full_name', e.target.value)}
                    required
                    data-testid="full-name-input"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleChange('email', e.target.value)}
                      required
                      data-testid="email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)}
                      required
                      data-testid="phone-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="City, State"
                    value={formData.location}
                    onChange={(e) => handleChange('location', e.target.value)}
                    required
                    data-testid="location-input"
                  />
                </div>

                <div>
                  <Label htmlFor="support_type">Type of Support Needed *</Label>
                  <Select 
                    value={formData.support_type} 
                    onValueChange={(value) => handleChange('support_type', value)}
                    required
                  >
                    <SelectTrigger data-testid="support-type-select">
                      <SelectValue placeholder="Select support type" />
                    </SelectTrigger>
                    <SelectContent>
                      {supportTypes.map((type) => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="description">Description of Situation *</Label>
                  <Textarea
                    id="description"
                    rows={5}
                    placeholder="Please describe your situation and what kind of support you're looking for..."
                    value={formData.description}
                    onChange={(e) => handleChange('description', e.target.value)}
                    required
                    data-testid="description-input"
                  />
                </div>

                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white py-6 text-lg"
                  data-testid="submit-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                  <Send size={20} className="ml-2" />
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default RequestSupport;
